// src/utils/firebase/checklist.js
import { 
  doc, 
  getDoc, 
  collection, 
  writeBatch, 
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase.utils';
import _ from 'lodash';
import { setChecklistData, appendChecklistData, setChecklistPagination, setChecklistCardIds, setParallelData, setChecklistMetadata } from '../../store/card/card.action';

// Store pagination cursors outside Redux (non-serializable)
const paginationCursors = new Map();

/**
 * Extracts a sortable numeric value from a card number
 * For numeric cards: extracts the numeric value (e.g., "1" → 1, "1-1" → 1, "123" → 123)
 * For non-numeric cards: returns 1000000 so they sort after all numeric cards
 * 
 * @param {string} cardNumber - The card number to extract numeric value from
 * @returns {number} Numeric value for sorting (0-999999 for numeric, 1000000 for non-numeric)
 */
export const getCardNumberSortValue = (cardNumber) => {
  if (!cardNumber) return 0;
  
  // Split by '-' and get the last part
  const parts = cardNumber.split('-');
  const lastPart = parts[parts.length - 1];
  
  // Extract numeric value from the last part
  const numericValue = parseInt(lastPart.replace(/^\D+/g, ''), 10);
  
  // If we found a numeric value, return it
  if (!isNaN(numericValue) && numericValue > 0) {
    return numericValue;
  }
  
  // For non-numeric cards, return a high number so they sort after numeric cards
  return 1000000;
};

/**
 * Adds checklist data to Firestore
 * @param {Object} checklistData - Checklist data to upload
 */
export const addChecklistToFirestore = async (checklistData) => {
  // Firestore batches are limited to 500 operations. Use a rolling batch that
  // auto-commits and resets whenever the limit is approached.
  const BATCH_LIMIT = 450;
  let currentBatch = writeBatch(db);
  let currentBatchSize = 0;

  const batchSet = async (ref, data, options) => {
    if (currentBatchSize >= BATCH_LIMIT) {
      await currentBatch.commit();
      currentBatch = writeBatch(db);
      currentBatchSize = 0;
    }
    if (options) {
      currentBatch.set(ref, data, options);
    } else {
      currentBatch.set(ref, data);
    }
    currentBatchSize++;
  };

  const flushBatch = async () => {
    if (currentBatchSize > 0) {
      await currentBatch.commit();
      currentBatch = writeBatch(db);
      currentBatchSize = 0;
    }
  };

  let newAvailableYears = {};
  let newAvailableSeries = {};
  let newAvailableCardTypeCategories = {};

  // Helper Function: Calculate totalCardCount by summing all cardCount fields within availableYear/Series/CardTypeCategories map
  const calculateTotalCardCount = (object) => {
    return Object.values(object).reduce((total, item) => total + (item.cardCount || 0), 0);
  };

  for (const sport of Object.keys(checklistData)) {
    const sportData = checklistData[sport];
    if (!sportData) continue;

    // fetch availableYears for each Sport
    const sportDocRef = doc(db, `checklists/${sport}`);
    try {
      const sportDocSnap = await getDoc(sportDocRef);
      if (sportDocSnap.exists()) {
        const data = sportDocSnap.data();
        newAvailableYears = _.cloneDeep(data.availableYears) || {};
      }
    } catch (error) {
      // Error fetching availableYears - continue with empty
    }

    for (const year of Object.keys(sportData)) {
      const yearData = sportData[year];
      if (!yearData) continue;

      for (const manufacturer of Object.keys(yearData)) {
        const manufacturerData = yearData[manufacturer];
        if (!manufacturerData) continue;

        // fetch availableSeries
        const manufacturerDocRef = doc(db, `checklists/${sport}/${year}/${manufacturer}`);
        try {
          const manufacturerDocSnap = await getDoc(manufacturerDocRef);
          if (manufacturerDocSnap.exists()) {
            const data = manufacturerDocSnap.data();
            newAvailableSeries = _.cloneDeep(data.availableSeries) || {};
          }
        } catch (error) {
          // Error fetching availableSeries - continue with empty
        }

        let manufacturerNewCardsCount = 0; // Track new cards for the manufacturer

        for (const series of Object.keys(manufacturerData)) {
          const seriesData = manufacturerData[series];
          if (!seriesData) continue;

          let seriesNewCardsCount = 0; // Track new cards for the series

          for (const cardType of Object.keys(seriesData)) {
            const cardTypeData = seriesData[cardType];
            if (!cardTypeData) continue;

            // fetch availableCardTypeCategories
            const cardTypeDocRef = doc(db, `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}`);
            try {
              const cardTypeDocSnap = await getDoc(cardTypeDocRef);
              if (cardTypeDocSnap.exists()) {
                const data = cardTypeDocSnap.data();
                newAvailableCardTypeCategories = _.cloneDeep(data.availableCardTypeCategories) || {};
              }
            } catch (error) {
              // Error fetching availableCardTypeCategories - continue with empty
            }

            for (const cardTypeCategory of Object.keys(cardTypeData)) {
              const cardTypeCategoryData = cardTypeData[cardTypeCategory];
              if (!cardTypeCategoryData) continue;

              // Check existing cards in the cards subcollection (new structure)
              // cardTypeCategory is a collection (position 7), so we need a document to hold the subcollection
              // Path: checklists/.../cardType (doc - 6) / cardTypeCategory (collection - 7) / _cards (doc - 8) / cards (collection - 9)
              const cardTypeCategoryCollectionRef = collection(db, 'checklists', sport, year, manufacturer, series, cardType, cardTypeCategory);
              const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards'); // Placeholder document for subcollection
              const cardsCollectionRef = collection(cardsDocRef, 'cards');
              const snapshot = await getDocs(cardsCollectionRef);
              const existingCardIds = snapshot.docs.map(doc => doc.id);
              const cardTypeCategoryKeys = Object.keys(cardTypeCategoryData);

              let newCardTypeCardsAdded = 0;

              cardTypeCategoryKeys.forEach((key) => {
                if (!existingCardIds.includes(key)) {
                  newCardTypeCardsAdded++;
                }
              });

              seriesNewCardsCount += newCardTypeCardsAdded;
              manufacturerNewCardsCount += newCardTypeCardsAdded;

              // Extract teams and count cards per team for metadata
              const teamsMap = {};
              
              // Add each new card to Firestore in a batch
              // Write to cards subcollection for queryable structure (reuse references from above)
              for (const cardNumber of Object.keys(cardTypeCategoryData)) {
                const cardData = cardTypeCategoryData[cardNumber];
                // Create document reference in the cards subcollection
                const cardDocRef = doc(cardsCollectionRef, cardNumber);

                // Ensure team is an array for Firestore array-contains queries
                const teamArray = Array.isArray(cardData.team) 
                  ? cardData.team 
                  : (cardData.team ? [cardData.team] : []);

                await batchSet(cardDocRef, {
                  cardNumber: cardData.cardNumber,
                  cardNumberSort: getCardNumberSortValue(cardData.cardNumber),
                  name: cardData.name,
                  team: teamArray,
                  cardAttributes: cardData.cardAttributes || [],
                });

                // Count teams for metadata (count all cards, not just new ones, to get accurate totals)
                teamArray.forEach(team => {
                  if (team) {
                    teamsMap[team] = (teamsMap[team] || 0) + 1;
                  }
                });
              }

              // Also count teams from existing cards to get complete team counts
              snapshot.forEach((doc) => {
                const existingCardData = doc.data();
                if (existingCardData.team && Array.isArray(existingCardData.team)) {
                  existingCardData.team.forEach(team => {
                    if (team) {
                      teamsMap[team] = (teamsMap[team] || 0) + 1;
                    }
                  });
                }
              });

              // Store teams map in metadata document
              const metadataDocRef = doc(cardTypeCategoryCollectionRef, '_metadata');
              await batchSet(metadataDocRef, {
                teams: teamsMap
              }, { merge: true });

              // Register this category in availableCardTypeCategories
              const totalCardsInCategory = Object.keys(cardTypeCategoryData).length;
              newAvailableCardTypeCategories[cardTypeCategory] = {
                ...(newAvailableCardTypeCategories[cardTypeCategory] || {}),
                cardCount: totalCardsInCategory,
                title: cardTypeCategory,
              };

            }

            const newTotalCardCount = calculateTotalCardCount(newAvailableCardTypeCategories);
            await batchSet(cardTypeDocRef, {
              availableCardTypeCategories: newAvailableCardTypeCategories,
              totalCardCount: newTotalCardCount,
            }, { merge: true });
            newAvailableCardTypeCategories = {}
          }

          if (newAvailableSeries[series]) {
            // If the year already exists, add the existing cardCount to manufacturerNewCardsCount
            newAvailableSeries[series].cardCount += seriesNewCardsCount;
          } else {
            // If the year does not exist, initialize it with manufacturerNewCardsCount
            newAvailableSeries[series] = {
              cardCount: seriesNewCardsCount,
              title: series,
            };
          }
        }
        const newTotalCardCount = calculateTotalCardCount(newAvailableSeries);
        await batchSet(manufacturerDocRef, {
          availableSeries: newAvailableSeries,
          totalCardCount: newTotalCardCount,
        }, { merge: true });
        if (newAvailableYears[year]) {
          newAvailableYears[year].cardCount += manufacturerNewCardsCount;
        } else {
          newAvailableYears[year] = {
            cardCount: manufacturerNewCardsCount,
            title: year,
          };
        }
      }
    }
    const newTotalCardCount = calculateTotalCardCount(newAvailableYears);
    await batchSet(sportDocRef, {
      availableYears: newAvailableYears,
      totalCardCount: newTotalCardCount,
    }, { merge: true });
  }
  await flushBatch();
};

/**
 * Adds parallel data to Firestore
 * @param {Object} parallelData - Parallel data to upload
 */
export const addParallelsToFirestore = async (parallelData) => {
  if (!parallelData || typeof parallelData !== 'object') {
    throw new Error('Invalid parallel data: parallelData must be an object');
  }

  const batch = writeBatch(db);
  let operationCount = 0;

  try {
    // Traverse through the structure of parallelData
    for (const sport of Object.keys(parallelData)) {
      const sportParallelData = parallelData[sport];
      if (!sportParallelData || typeof sportParallelData !== 'object') continue;

      for (const year of Object.keys(sportParallelData)) {
        const yearParallelData = sportParallelData[year];
        if (!yearParallelData || typeof yearParallelData !== 'object') continue;

        for (const manufacturer of Object.keys(yearParallelData)) {
          const manufacturerParallelData = yearParallelData[manufacturer];
          if (!manufacturerParallelData || typeof manufacturerParallelData !== 'object') continue;

          // Fetch availableSeries from checklist to get the correct series keys
          // This ensures we use the same series key that was used when the checklist was uploaded
          let checklistAvailableSeries = {};
          const checklistManufacturerDocRef = doc(db, `checklists/${sport}/${year}/${manufacturer}`);
          try {
            const checklistManufacturerDocSnap = await getDoc(checklistManufacturerDocRef);
            if (checklistManufacturerDocSnap.exists()) {
              const checklistData = checklistManufacturerDocSnap.data();
              checklistAvailableSeries = checklistData.availableSeries || {};
            }
          } catch (error) {
            // Error fetching availableSeries - continue
          }

          for (const series of Object.keys(manufacturerParallelData)) {
            const seriesParallelData = manufacturerParallelData[series];
            if (!seriesParallelData || typeof seriesParallelData !== 'object') continue;

            // Normalize series key to match the one used in the checklist
            // This prevents duplicate subcollections when the series key has different characters (e.g., different hyphens)
            let normalizedSeriesKey = null;
            
            // Try to find matching series by exact key match first
            if (checklistAvailableSeries[series]) {
              normalizedSeriesKey = series; // Exact match, use as-is
            } else {
              // Try to find matching series by title
              for (const [key, value] of Object.entries(checklistAvailableSeries)) {
                if (value.title === series) {
                  normalizedSeriesKey = key; // Use the checklist's key
                  break;
                }
              }
              
              // If no match found, try to normalize common character differences
              if (!normalizedSeriesKey && Object.keys(checklistAvailableSeries).length > 0) {
                // More aggressive normalization: replace ALL non-ASCII dash/hyphen-like characters with regular hyphen
                // This includes: en-dash, em-dash, horizontal bar, figure dash, minus sign, non-breaking hyphen, etc.
                const normalizeDash = (str) => {
                  return str
                    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-') // All dash/hyphen variants
                    .replace(/\u00AD/g, '') // Remove soft hyphens
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim();
                };
                
                const normalizedParallelSeries = normalizeDash(series);
                
                for (const [key, value] of Object.entries(checklistAvailableSeries)) {
                  const normalizedChecklistKey = normalizeDash(key);
                  const normalizedChecklistTitle = normalizeDash(value.title || '');
                  
                  // Compare normalized versions (case-insensitive for extra safety)
                  if (normalizedChecklistKey.toLowerCase() === normalizedParallelSeries.toLowerCase() || 
                      normalizedChecklistTitle.toLowerCase() === normalizedParallelSeries.toLowerCase()) {
                    normalizedSeriesKey = key; // Use the checklist's key
                    break;
                  }
                }
              }
            }
            
            // Skip this series if we couldn't find a match in the checklist
            // This prevents creating duplicate subcollections for series that don't exist in the checklist
            if (!normalizedSeriesKey) {
              continue;
            }

            for (const cardType of Object.keys(seriesParallelData)) {
              const cardTypeParallelData = seriesParallelData[cardType];
              if (!cardTypeParallelData || typeof cardTypeParallelData !== 'object') continue;

              for (const cardTypeCategory of Object.keys(cardTypeParallelData)) {
                const cardTypeCategoryParallelData = cardTypeParallelData[cardTypeCategory];

                // Store parallel data in metadata document
                // cardTypeCategory is a collection, so _metadata is a document in that collection
                // Path: checklists/.../cardType (doc - 6) / cardTypeCategory (collection - 7) / _metadata (doc - 8)
                // Use normalizedSeriesKey to ensure we use the same series key as the checklist
                const cardTypeCategoryCollectionRef = collection(db, 'checklists', sport, year, manufacturer, normalizedSeriesKey, cardType, cardTypeCategory);
                const metadataDocRef = doc(cardTypeCategoryCollectionRef, '_metadata');

                // Set the availableParallels and sortOrder fields in the metadata document
                // sortOrder: 'numeric' or 'alphabetical' - determines how cards should be sorted
                // If cardTypeCategoryParallelData is an array, extract sortOrder separately
                // Otherwise, it might be an object with sortOrder property
                let parallelDataArray = cardTypeCategoryParallelData;
                let sortOrder = 'numeric'; // Default to numeric
                
                if (cardTypeCategoryParallelData && typeof cardTypeCategoryParallelData === 'object' && !Array.isArray(cardTypeCategoryParallelData)) {
                  // If it's an object, check for sortOrder property
                  if (cardTypeCategoryParallelData.sortOrder) {
                    sortOrder = cardTypeCategoryParallelData.sortOrder;
                  }
                  // Extract the actual parallel data (could be in 'parallels' or 'availableParallels' or use the whole object)
                  parallelDataArray = cardTypeCategoryParallelData.parallels || cardTypeCategoryParallelData.availableParallels || Object.values(cardTypeCategoryParallelData);
                }
                
                // Ensure parallelDataArray is an array
                if (!Array.isArray(parallelDataArray)) {
                  continue;
                }

                const dataToWrite = {
                  availableParallels: parallelDataArray,
                  sortOrder: sortOrder
                };

                batch.set(metadataDocRef, dataToWrite, { merge: true });
                operationCount++;
              }
            }
          }
        }
      }
    }

    if (operationCount === 0) {
      throw new Error('No parallel data operations to commit. Check the data structure.');
    }

    await batch.commit();
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches available sports
 * @param {string|null} userID - User ID (optional)
 * @returns {Promise<Array>} Array of sports data
 */
export const getAvailableSports = async (userID = null) => {
  try {
    const collectionRef = userID
      ? collection(db, `users/${userID}/collection`)
      : collection(db, 'checklists');

    const querySnapshot = await getDocs(collectionRef)

    if (querySnapshot.empty) return [];

    const sportsData = querySnapshot.docs.map((doc) => {
      const { totalCardCount = 0 } = doc.data();
      return { sport: doc.id, totalCardCount };
    });

    return sportsData;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches available years for a sport
 * @param {string|null} userID - User ID (optional)
 * @param {string} sport - Sport
 * @returns {Promise<Array>} Array of years data
 */
export const getAvailableYears = async (userID = null, sport) => {
  try {
    let sportDocRef;
    let yearsField = 'availableYears';

    if (userID) {
      sportDocRef = doc(db, `users/${userID}/collection`, sport);
      yearsField = 'collectedYears';
    } else {
      sportDocRef = doc(db, 'checklists', sport);
    }
    
    const sportDocSnap = await getDoc(sportDocRef);

    if (sportDocSnap.exists()) {
      const data = sportDocSnap.data();
      const yearsData = data[yearsField];
      return yearsData || [];
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches available manufacturers for a sport and year
 * @param {string|null} userID - User ID (optional)
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @returns {Promise<Array>} Array of manufacturers data
 */
export const getAvailableManufacturers = async (userID = null, sport, year) => {
  try {
    let manufacturerRef;

    if (userID) {
      manufacturerRef = collection(db, `users/${userID}/collection/${sport}/${year}`);
    } else {
      manufacturerRef = collection(db, `checklists/${sport}/${year}`);
    }

    const manufacturerSnap = await getDocs(manufacturerRef);

    if (!manufacturerSnap.empty) {
      const manufacturersData = await Promise.all(
        manufacturerSnap.docs.map(async (doc) => {
          const manufacturer = doc.id;
          const manufacturerDocRef = doc.ref;
          const manufacturerSnapshot = await getDoc(manufacturerDocRef);

          if (manufacturerSnapshot.exists()) {
            const { totalCardCount } = manufacturerSnapshot.data();
            return { manufacturer, totalCardCount: totalCardCount || 0 };
          } else {
            return { manufacturer, totalCardCount: 0 };
          }
        })
      );
      return manufacturersData;
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches available series for a manufacturer
 * @param {string|null} userID - User ID (optional)
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string|null} type - Type (optional)
 * @returns {Promise<Array>} Array of series data
 */
export const getAvailableSeries = async (userID = null, sport, year, manufacturer, type = null) => {
  try {
    let manufacturerDocRef;
    let seriesField = 'availableSeries';

    // For checklist view, always query checklists, not user collection
    if (userID && type !== "view") {
      manufacturerDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}`);
      seriesField = 'collectedSeries';
    } else {
      manufacturerDocRef = doc(db, `checklists/${sport}/${year}/${manufacturer}`);
    }

    const manufacturerDocSnap = await getDoc(manufacturerDocRef);

    if (manufacturerDocSnap.exists()) {
      const data = manufacturerDocSnap.data();
      const seriesData = data[seriesField];
      return seriesData || [];
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * Fetches a single series data (cardCount and releaseDate)
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series name
 * @returns {Promise<Object|null>} Series data object or null if not found
 */
export const getSeriesData = async (sport, year, manufacturer, series) => {
  try {
    const manufacturerDocRef = doc(db, `checklists/${sport}/${year}/${manufacturer}`);
    const manufacturerDocSnap = await getDoc(manufacturerDocRef);

    if (manufacturerDocSnap.exists()) {
      const data = manufacturerDocSnap.data();
      const availableSeries = data.availableSeries || {};
      // Return only the specific series data we need
      return availableSeries[series] || null;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Fetches available card types for a series
 * @param {string|null} userID - User ID (optional)
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @returns {Promise<Array>} Array of card types data
 */
export const getAvailableCardTypes = async (userID = null, sport, year, manufacturer, series) => {
  try {
    let cardTypesRef;

    if (userID) {
      cardTypesRef = collection(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}`);
    } else {
      cardTypesRef = collection(db, `checklists/${sport}/${year}/${manufacturer}/${series}`);
    }

    const cardTypeSnap = await getDocs(cardTypesRef);

    if (!cardTypeSnap.empty) {
      const cardTypesData = await Promise.all(
        cardTypeSnap.docs.map(async (doc) => {
          const cardType = doc.id;
          const cardTypeDocRef = doc.ref;
          const cardTypeSnapshot = await getDoc(cardTypeDocRef);

          if (cardTypeSnapshot.exists()) {
            const { totalCardCount } = cardTypeSnapshot.data();
            return { cardType, totalCardCount: totalCardCount || 0 };
          } else {
            return { cardType, totalCardCount: 0 };
          }
        })
      );
      return cardTypesData;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * Fetches available card type categories for a card type
 * @param {string|null} userID - User ID (optional)
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @returns {Promise<Array>} Array of card type categories data
 */
export const getAvailableCardTypeCategories = async (userID = null, sport, year, manufacturer, series, cardType) => {
  try {
    let cardTypeRef;
    let cardTypeCategoryField = 'availableCardTypeCategories';

    if (userID) {
      cardTypeRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}`);
      cardTypeCategoryField = 'collectedCardTypeCategories';
    } else {
      cardTypeRef = doc(db, `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}`);
    }

    const cardTypeCategoriesSnap = await getDoc(cardTypeRef);

    if (cardTypeCategoriesSnap.exists()) {
      const data = cardTypeCategoriesSnap.data();
      const cardTypeCategories = data[cardTypeCategoryField];
      return cardTypeCategories || [];
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * Fetches parallel data and metadata for a category - returns data without dispatching.
 * Use for cancellation support when params may change before fetch completes.
 *
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} cardTypeCategory - Card type category
 * @returns {Promise<{ parallelData: Array, metadata: Object }>}
 */
export const getParallelAndMetadataData = async (sport, year, manufacturer, series, cardType, cardTypeCategory) => {
  try {
    const cardTypeCategoryCollectionRef = collection(db, 'checklists', sport, year, manufacturer, series, cardType, cardTypeCategory);
    const metadataDocRef = doc(cardTypeCategoryCollectionRef, '_metadata');
    const metadataDocSnap = await getDoc(metadataDocRef);

    let parallelData = [];
    let metadata = { sortOrder: 'numeric', teams: {} };

    if (metadataDocSnap.exists()) {
      const metadataData = metadataDocSnap.data();
      if (metadataData.sortOrder) metadata.sortOrder = metadataData.sortOrder;
      if (metadataData.teams && typeof metadataData.teams === 'object') metadata.teams = metadataData.teams;
      if (metadataData.availableParallels) {
        parallelData = Array.isArray(metadataData.availableParallels)
          ? metadataData.availableParallels
          : Object.values(metadataData.availableParallels);
      }
    }
    return { parallelData, metadata };
  } catch (error) {
    return { parallelData: [], metadata: { sortOrder: 'numeric', teams: {} } };
  }
};

/**
 * Fetches parallel data for a specific cardTypeCategory
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} cardTypeCategory - Card type category
 * @returns {Function} Redux thunk function
 */
export const fetchParallelData = (sport, year, manufacturer, series, cardType, cardTypeCategory) => async (dispatch) => {
  try {
    // Fetch parallel data from metadata document
    // cardTypeCategory is a collection, so _metadata is a document in that collection
    // Path: checklists/.../cardType (doc - 6) / cardTypeCategory (collection - 7) / _metadata (doc - 8)
    const cardTypeCategoryCollectionRef = collection(db, 'checklists', sport, year, manufacturer, series, cardType, cardTypeCategory);
    const metadataDocRef = doc(cardTypeCategoryCollectionRef, '_metadata');
    const metadataDocSnap = await getDoc(metadataDocRef);
    
    let parallelData = [];
    let metadata = { sortOrder: 'numeric' };
    
    if (metadataDocSnap.exists()) {
      const metadataData = metadataDocSnap.data();
      
      // Extract sortOrder from metadata
      if (metadataData.sortOrder) {
        metadata.sortOrder = metadataData.sortOrder;
      }
      
      // Extract teams from metadata
      if (metadataData.teams && typeof metadataData.teams === 'object') {
        metadata.teams = metadataData.teams;
      }
      
      if (metadataData.availableParallels) {
        // availableParallels should already be an array with the correct format
        if (Array.isArray(metadataData.availableParallels)) {
          parallelData = metadataData.availableParallels;
        } else {
          // If it's an object, convert to array
          parallelData = Object.values(metadataData.availableParallels);
        }
      }
    }

    dispatch(setParallelData(parallelData));
    dispatch(setChecklistMetadata(metadata));
  } catch (error) {
    dispatch(setParallelData([]));
  }
};

/**
 * Fetches all card IDs (lightweight) for a checklist - returns data without dispatching.
 * Use for cancellation support when params may change before fetch completes.
 *
 * @param {string} checklistPath - Checklist path
 * @param {Object} options - Query options
 * @param {string} options.team - Filter by team (uses array-contains)
 * @param {string} options.sortOrder - Sort order: 'numeric' or 'alphabetical' (from metadata)
 * @returns {Promise<string[]>} Array of card IDs
 */
export const getChecklistCardIdsData = async (checklistPath, options = {}) => {
  try {
    const { team, sortOrder = 'numeric' } = options;
    const pathSegments = checklistPath.split('/');
    const cardTypeCategoryCollectionRef = collection(db, ...pathSegments);
    const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards');
    const cardsRef = collection(cardsDocRef, 'cards');
    let cardsQuery = query(cardsRef);
    if (sortOrder === 'numeric') {
      cardsQuery = query(cardsQuery, orderBy('cardNumberSort', 'asc'));
    } else {
      cardsQuery = query(cardsQuery, orderBy('cardNumber', 'asc'));
    }
    if (team && team !== 'All Teams') {
      cardsQuery = query(cardsQuery, where('team', 'array-contains', team));
    }
    const cardsSnapshot = await getDocs(cardsQuery);
    return cardsSnapshot.docs.map((d) => d.id);
  } catch (error) {
    return [];
  }
};

/**
 * Fetches checklist data for specific card IDs - returns data without dispatching.
 *
 * @param {string} checklistPath - Checklist path
 * @param {Array<string>} cardIds - Array of card IDs to fetch
 * @returns {Promise<Object>} Checklist data keyed by card ID
 */
export const getChecklistDataByIdsData = async (checklistPath, cardIds) => {
  try {
    if (!cardIds || cardIds.length === 0) return {};
    const pathSegments = checklistPath.split('/');
    const cardTypeCategoryCollectionRef = collection(db, ...pathSegments);
    const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards');
    const cardsRef = collection(cardsDocRef, 'cards');
    const checklistData = {};
    const batchSize = 10;
    for (let i = 0; i < cardIds.length; i += batchSize) {
      const batch = cardIds.slice(i, i + batchSize);
      const docRefs = batch.map((cardId) => doc(cardsRef, cardId));
      const docSnaps = await Promise.all(docRefs.map((docRef) => getDoc(docRef)));
      docSnaps.forEach((docSnap, index) => {
        if (docSnap.exists()) {
          checklistData[batch[index]] = docSnap.data();
        }
      });
    }
    return checklistData;
  } catch (error) {
    return {};
  }
};

/**
 * Fetches all card IDs (lightweight) for a checklist
 * Used for pagination calculation and search across all cards
 * 
 * @param {string} checklistPath - Checklist path
 * @param {Object} options - Query options
 * @param {string} options.team - Filter by team (uses array-contains)
 * @param {string} options.sortOrder - Sort order: 'numeric' or 'alphabetical' (from metadata)
 * @returns {Function} Redux thunk function
 */
export const fetchChecklistCardIds = (checklistPath, options = {}) => async (dispatch) => {
  try {
    const { team, sortOrder = 'numeric' } = options;
    
    // New structure: cards subcollection
    const pathSegments = checklistPath.split('/');
    const cardTypeCategoryCollectionRef = collection(db, ...pathSegments);
    const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards');
    const cardsRef = collection(cardsDocRef, 'cards');
    
    // Build query for IDs only
    let cardsQuery = query(cardsRef);
    
    // Add sorting
    if (sortOrder === 'numeric') {
      cardsQuery = query(cardsQuery, orderBy('cardNumberSort', 'asc'));
    } else {
      cardsQuery = query(cardsQuery, orderBy('cardNumber', 'asc'));
    }
    
    // Add team filter if specified
    if (team && team !== 'All Teams') {
      cardsQuery = query(cardsQuery, where('team', 'array-contains', team));
    }
    
    // Fetch all document IDs (lightweight - no data)
    const cardsSnapshot = await getDocs(cardsQuery);
    const cardIds = cardsSnapshot.docs.map(doc => doc.id);
    
    dispatch(setChecklistCardIds(cardIds));
  } catch (error) {
    dispatch(setChecklistCardIds([]));
  }
};

/**
 * Fetches checklist data for specific card IDs
 * 
 * @param {string} checklistPath - Checklist path
 * @param {Array<string>} cardIds - Array of card IDs to fetch
 * @returns {Function} Redux thunk function
 */
export const fetchChecklistDataByIds = (checklistPath, cardIds) => async (dispatch) => {
  try {
    if (!cardIds || cardIds.length === 0) {
      dispatch(setChecklistData({}));
      return;
    }
    
    const pathSegments = checklistPath.split('/');
    const cardTypeCategoryCollectionRef = collection(db, ...pathSegments);
    const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards');
    const cardsRef = collection(cardsDocRef, 'cards');
    
    // Fetch documents by IDs
    // Note: Firestore doesn't support fetching multiple documents by ID in a single query efficiently
    // We'll need to fetch them individually or in batches
    const checklistData = {};
    
    // Fetch in batches of 10 (Firestore limit for getDocs with multiple doc refs)
    const batchSize = 10;
    for (let i = 0; i < cardIds.length; i += batchSize) {
      const batch = cardIds.slice(i, i + batchSize);
      const docRefs = batch.map(cardId => doc(cardsRef, cardId));
      const docSnaps = await Promise.all(docRefs.map(docRef => getDoc(docRef)));
      
      docSnaps.forEach((docSnap, index) => {
        if (docSnap.exists()) {
          checklistData[batch[index]] = docSnap.data();
        }
      });
    }
    
    // Append to existing data instead of replacing
    dispatch(appendChecklistData(checklistData));
  } catch (error) {
    dispatch(appendChecklistData({}));
  }
};

/**
 * Fetches checklist data and dispatches to Redux
 * Supports both new structure (cards subcollection with queries) and old structure (backward compatibility)
 * 
 * @param {string} checklistPath - Checklist path
 * @param {Object} options - Query options
 * @param {string} options.team - Filter by team (uses array-contains)
 * @param {number} options.limitCount - Limit number of results (for pagination)
 * @param {Object} options.lastDoc - Last document snapshot for pagination (startAfter)
 * @param {string} options.sortOrder - Sort order: 'numeric' or 'alphabetical' (from metadata)
 * @param {boolean} options.append - Whether to append results to existing data (default: false)
 * @returns {Function} Redux thunk function
 */
export const fetchChecklistData = (checklistPath, options = {}) => async (dispatch) => {
  try {
    const { team, limitCount = 50, lastDoc: providedLastDoc, sortOrder = 'numeric', append = false } = options;
    
    // Get or create cursor key for this checklist path
    const cursorKey = `${checklistPath}:${team || 'all'}:${sortOrder}`;
    
    // Use provided lastDoc or get from cache
    const lastDoc = providedLastDoc || paginationCursors.get(cursorKey);
    
    // New structure: cards subcollection
    // checklistPath ends at cardTypeCategory (collection - position 7)
    // Path structure: checklists/.../cardType (doc - 6) / cardTypeCategory (collection - 7) / _cards (doc - 8) / cards (collection - 9)
    const pathSegments = checklistPath.split('/');
    // Get the collection reference for cardTypeCategory
    const cardTypeCategoryCollectionRef = collection(db, ...pathSegments);
    // Get the placeholder document for the cards subcollection
    const cardsDocRef = doc(cardTypeCategoryCollectionRef, '_cards');
    // Access the cards subcollection
    const cardsRef = collection(cardsDocRef, 'cards');
    
    // Build query for new structure
    let cardsQuery = query(cardsRef);
    
    // Add sorting first (required before filters)
    if (sortOrder === 'numeric') {
      cardsQuery = query(cardsQuery, orderBy('cardNumberSort', 'asc'));
    } else {
      // For alphabetical, sort by cardNumber string
      cardsQuery = query(cardsQuery, orderBy('cardNumber', 'asc'));
    }
    
    // Add team filter if specified (must come after orderBy)
    if (team && team !== 'All Teams') {
      cardsQuery = query(cardsQuery, where('team', 'array-contains', team));
    }
    
    // Add pagination limit
    cardsQuery = query(cardsQuery, limit(limitCount));
    
    // Add startAfter for pagination if specified
    if (lastDoc) {
      cardsQuery = query(cardsQuery, startAfter(lastDoc));
    }
    
    dispatch(setChecklistPagination({ isLoading: true }));
    
    const cardsSnapshot = await getDocs(cardsQuery);
    
    const checklistData = {};
    // Don't filter here - we'll filter client-side to maintain cursor accuracy
    // The cursor needs to be based on ALL fetched documents, not just filtered ones
    cardsSnapshot.forEach((doc) => {
      checklistData[doc.id] = doc.data();
    });
    
    // Store the last document for next pagination (based on ALL fetched docs, not filtered)
    const lastDocument = cardsSnapshot.docs[cardsSnapshot.docs.length - 1];
    const hasMore = cardsSnapshot.docs.length === limitCount; // If we got a full page, there might be more
    
    if (lastDocument) {
      paginationCursors.set(cursorKey, lastDocument);
    } else {
      paginationCursors.delete(cursorKey);
    }
    
    // Dispatch results
    if (append) {
      dispatch(appendChecklistData(checklistData));
    } else {
      // Clear cursor on new fetch
      paginationCursors.delete(cursorKey);
      if (lastDocument) {
        paginationCursors.set(cursorKey, lastDocument);
      }
      dispatch(setChecklistData(checklistData));
    }
    
    // Update pagination state
    dispatch(setChecklistPagination({ 
      hasMore, 
      isLoading: false,
      lastFetchedCount: Object.keys(checklistData).length
    }));
  } catch (error) {
    if (error.code === 'failed-precondition') {
      alert('Error: Firestore index required. Please create the required index in Firebase Console.');
    } else {
      alert(`Error fetching checklist data: ${error.message}`);
    }
    dispatch(setChecklistData({}));
  }
};

/**
 * Creates a custom card type category for a user
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} categoryName - Category name
 * @returns {Promise<Object>} Created category data
 */
export const createCustomCardTypeCategory = async (userID, sport, year, manufacturer, series, cardType, categoryName) => {
  try {
    // Validate required fields
    if (!categoryName || !categoryName.trim()) {
      throw new Error('Category name is required');
    }

    // Generate a unique category ID using timestamp and random string
    const categoryId = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create the category document
    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/customCategories/{categoryId}
    const categoryPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/customCategories/${categoryId}`;
    const categoryDocRef = doc(db, categoryPath);

    const categoryDocument = {
      name: categoryName.trim(),
      createdAt: new Date().toISOString(),
      cardCount: 0
    };

    await setDoc(categoryDocRef, categoryDocument);

    return { 
      id: categoryId, 
      name: categoryName.trim(),
      cardCount: 0,
      isCustom: true
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches custom card type categories for a user
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @returns {Promise<Array>} Array of custom categories
 */
export const getCustomCardTypeCategories = async (userID, sport, year, manufacturer, series, cardType) => {
  try {
    if (!userID) {
      return [];
    }

    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/customCategories
    const customCategoriesPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/customCategories`;
    const customCategoriesCollectionRef = collection(db, customCategoriesPath);
    const snapshot = await getDocs(customCategoriesCollectionRef);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name,
      cardCount: doc.data().cardCount || 0,
      isCustom: true
    }));
  } catch (error) {
    return [];
  }
};

/**
 * Updates a custom card type category name
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} categoryId - Category ID
 * @param {string} newName - New category name
 * @returns {Promise<Object>} Updated category data
 */
export const updateCustomCardTypeCategory = async (userID, sport, year, manufacturer, series, cardType, categoryId, newName) => {
  try {
    // Validate required fields
    if (!newName || !newName.trim()) {
      throw new Error('Category name is required');
    }

    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/customCategories/{categoryId}
    const categoryPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/customCategories/${categoryId}`;
    const categoryDocRef = doc(db, categoryPath);

    // Update the category name
    await setDoc(categoryDocRef, {
      name: newName.trim()
    }, { merge: true });

    return { 
      id: categoryId, 
      name: newName.trim()
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Counts cards in a custom category
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} categoryId - Category ID
 * @returns {Promise<number>} Number of cards in the category
 */
export const countCardsInCustomCategory = async (userID, sport, year, manufacturer, series, cardType, categoryId) => {
  try {
    // Construct the full category path with "custom:" prefix for the cards collection
    // Cards are stored at: users/.../cardType/custom:custom_1762526340325_oeepj0tvm/cardNumber
    const categoryPathWithPrefix = `custom:${categoryId}`;
    
    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/custom:custom_...
    // Cards are stored as documents in this collection
    const cardsPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${categoryPathWithPrefix}`;
    const cardsCollectionRef = collection(db, cardsPath);
    const snapshot = await getDocs(cardsCollectionRef);
    
    return snapshot.size;
  } catch (error) {
    return 0;
  }
};

/**
 * Fetches card IDs from a custom category
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} categoryId - Category ID
 * @returns {Promise<Array<string>>} Array of card IDs
 */
export const fetchCustomCategoryCardIds = async (userID, sport, year, manufacturer, series, cardType, categoryIdOrPath) => {
  try {
    // categoryIdOrPath can be either:
    // 1. Just the ID (e.g., "custom_1762526340325_oeepj0tvm") - for backwards compatibility
    // 2. The full path with prefix (e.g., "custom:custom_1762526340325_oeepj0tvm") - actual Firestore path
    // Use it directly as it represents the actual Firestore collection name
    const cardsPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${categoryIdOrPath}`;
    const cardsCollectionRef = collection(db, cardsPath);
    const snapshot = await getDocs(cardsCollectionRef);
    
    // Extract card numbers (document IDs) and sort them
    const cardIds = snapshot.docs.map(doc => doc.id);
    
    // Sort by numeric value
    cardIds.sort((a, b) => {
      const aNum = parseInt(a.replace(/^\D+/g, ''), 10) || 0;
      const bNum = parseInt(b.replace(/^\D+/g, ''), 10) || 0;
      return aNum - bNum;
    });
    
    return cardIds;
  } catch (error) {
    return [];
  }
};

/**
 * Deletes a custom card type category and all its cards
 * @param {string} userID - User ID
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} categoryId - Category ID
 * @returns {Promise<void>}
 */
export const deleteCustomCardTypeCategory = async (userID, sport, year, manufacturer, series, cardType, categoryId) => {
  try {
    const batch = writeBatch(db);

    // Construct the full category path with "custom:" prefix for the cards collection
    // Cards are stored at: users/.../cardType/custom:custom_1762526340325_oeepj0tvm/cardNumber
    const categoryPathWithPrefix = `custom:${categoryId}`;
    
    // Delete all cards in the category
    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/custom:custom_...
    const cardsPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${categoryPathWithPrefix}`;
    const cardsCollectionRef = collection(db, cardsPath);
    const cardsSnapshot = await getDocs(cardsCollectionRef);
    
    // Add all card deletions to batch
    cardsSnapshot.docs.forEach((cardDoc) => {
      batch.delete(cardDoc.ref);
    });

    // Delete the category document
    // Path: users/{userId}/collection/{sport}/{year}/{manufacturer}/{series}/{cardType}/customCategories/{categoryId}
    // Note: The category document uses just the ID without the prefix
    const categoryPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/customCategories/${categoryId}`;
    const categoryDocRef = doc(db, categoryPath);
    batch.delete(categoryDocRef);

    // Execute all deletions in a single batch
    await batch.commit();
  } catch (error) {
    throw error;
  }
};
